import { Auth } from "aws-amplify";

/**
 * Returns a valid Cognito access-token JWT.
 * `Auth.currentSession()` transparently refreshes the token when it is expired and a
 * valid refresh token exists, so this is the default getter used by the AppSync clients.
 * @returns {Promise<string>} access token JWT
 */
export const getAccessJwtToken = async () => {
  const session = await Auth.currentSession();
  return session.getAccessToken().getJwtToken();
};

/**
 * Forces a brand-new Cognito session using the refresh token, bypassing the cached
 * (possibly stale or edge-expired) access token. Used to recover from a 401 at the
 * AppSync authorizer boundary before retrying a mutation.
 * @returns {Promise<string>} freshly minted access token JWT
 */
export const refreshAuthSession = async () => {
  const cognitoUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
  const currentSession = await Auth.currentSession();
  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(currentSession.getRefreshToken(), (error, session) => {
      if (error) {
        reject(error);
        return;
      }
      resolve(session.getAccessToken().getJwtToken());
    });
  });
};

/**
 * Detects whether an error raised by an AppSync/Apollo operation is an authentication
 * failure (HTTP 401 / Unauthorized) raised at the API authorizer boundary.
 * @param {*} error error thrown by client.mutate / client.query
 * @returns {boolean} true when the error is an authentication/authorization failure
 */
export const isAuthError = (error) => {
  if (!error) {
    return false;
  }

  if (error?.networkError?.statusCode === 401) {
    return true;
  }

  const messages = [
    error?.message,
    error?.networkError?.message,
    ...(Array.isArray(error?.graphQLErrors) ? error.graphQLErrors.map((graphQLError) => graphQLError?.message) : [])
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    messages.includes("401") ||
    messages.includes("unauthorized") ||
    messages.includes("not authorized") ||
    messages.includes("no current user")
  );
};
